import { createReducer, on } from '@ngrx/store';
import {
  ContactPointActionTypes,
  ContactPointFormActionTypes,
  ContactPointResendVerifyCodeActionTypes,
  ContactPointConfirmVerificationCodeActions,
} from './contact-point.actions';
import {
  PhoneNumberFailure,
  UsernameFailure,
  VerifiableUserAttributeKey,
} from '@fantaz/shared/data-access/models';
import { addMinutes } from 'date-fns';

export const CONTACT_POINT__FEATURE_KEY = 'profile-contact-point';

/**
 * Represents the state of a contact point.
 */
export type ContactPointState = {
  /**
   * The attribute name of the contact point. It can be either `email` or
   * `phone_number`.
   */
  attr?: VerifiableUserAttributeKey;

  /**
   * The value of the attribute.
   */
  value?: string;

  /**
   * Whether the contact point is valid after being processed.
   */
  valid?: boolean;

  /**
   * Whether the contact point is being processed.
   */
  processing?: boolean;

  /**
   * Whether the verification code is being resent.
   */
  resending?: boolean;

  /**
   * Whether the verification code has been sent.
   */
  sended?: boolean;

  /**
   * The number of times the verification code has been sent.
   */
  sendVerificationCodeCount?: number;

  /**
   * The countdown time in milliseconds.
   */
  countdown?: number;

  /**
   * The error that occurred during the process.
   */
  formErrors?: Record<string, Record<string, boolean>> | Record<string, any>;
};

const initialState: ContactPointState = {
  resending: false,
  sended: false,
  formErrors: {},
  valid: false,
};

export const contactPointReducer = createReducer<ContactPointState>(
  initialState,
  on(
    ContactPointFormActionTypes.initVerification,
    (state, { attr, value }) => ({
      ...state,
      ...initialState,
      attr,
      value,
    }),
  ),
  // ***************************************************************************
  // *** Validate Contact Point
  // ***************************************************************************
  on(
    ContactPointFormActionTypes.savingContactPointInProgress,
    (state, { attr, value }) => ({
      ...state,
      processing: true,
      attr,
      value,
    }),
  ),
  on(
    ContactPointFormActionTypes.savingContactPointSuccess,
    (state, { attr, value }) => ({
      ...state,
      processing: false,
      attr,
      value,
    }),
  ),
  on(
    ContactPointFormActionTypes.savingContactPointFailure,
    (state, { attr, value, error }) => ({
      ...state,
      processing: false,
      formErrors: {
        ...(error instanceof UsernameFailure ||
        error instanceof PhoneNumberFailure
          ? {
              contact: {
                [error.key]: true,
              },
            }
          : {}),

        contact: {
          [error.key]: true,
        },
      },
    }),
  ),
  // ***************************************************************************
  // *** Add Contact Point
  // ***************************************************************************
  on(
    ContactPointActionTypes.addContactPointInProgress,
    (state, { attr, value }) => ({
      ...state,
      ...initialState,
      processing: true,
      attr: attr,
      value,
    }),
  ),
  on(
    ContactPointActionTypes.addContactPointSuccess,
    (state, { attr, value }) => ({
      ...state,
      processing: false,
      sended: true,
      sendVerificationCodeCount: 1,
    }),
  ),
  on(
    ContactPointActionTypes.addContactPointFailure,
    (state, { attr, value, error }) => ({
      ...state,
      processing: false,
      formErrors: {
        ...(error instanceof UsernameFailure ||
        error instanceof PhoneNumberFailure
          ? {
              contact: {
                [error.key]: true,
              },
            }
          : {}),

        contact: {
          [error.key]: true,
        },
      },
    }),
  ),

  // ***************************************************************************
  // *** Confirm Verification Code
  // ***************************************************************************
  on(
    ContactPointConfirmVerificationCodeActions.confirmVerificationCodeInProgress,
    (state, { attr, code }) => ({
      ...state,
      attr,
      code,
      processing: true,
    }),
  ),
  on(
    ContactPointConfirmVerificationCodeActions.confirmVerificationCodeSuccess,
    (state, { attr, code }) => ({
      ...state,
      processing: false,
      error: null,
      formErrors: {},
    }),
  ),
  on(
    ContactPointConfirmVerificationCodeActions.confirmVerificationCodeFailure,
    (state, { attr, code, error }) => ({
      ...state,
      processing: false,
      formErrors: {
        ...state.formErrors,
        code: {
          [getErrorKey(error.name)]: true,
        },
      },
    }),
  ),
  // ***************************************************************************
  // *** Resend Verification Code
  // ***************************************************************************
  on(
    ContactPointResendVerifyCodeActionTypes.resendVerifyCodeInProgress,
    (state) => ({
      ...state,
      resending: true,
    }),
  ),
  on(
    ContactPointResendVerifyCodeActionTypes.resendVerifyCodeSuccess,
    (state) => {
      const times = state.sendVerificationCodeCount ?? 0 + 1;
      // 1
      // 5
      const minutes = (times * times + 1) / 2;

      return {
        ...state,
        resending: false,
        sendVerificationCodeCount: (state.sendVerificationCodeCount ?? 0) + 1,
        countdown: addMinutes(new Date(), minutes).getTime(),
      };
    },
  ),
  on(
    ContactPointResendVerifyCodeActionTypes.resendVerifyCodeFailure,
    (state) => ({
      ...state,
      resending: false,
    }),
  ),
);

function getErrorKey(text: string) {
  return text
    .replace('Exception', '')
    .replace(/\s(.)/g, function (a) {
      return a.toUpperCase();
    })
    .replace(/\s/g, '')
    .replace(/^(.)/, function (b) {
      return b.toLowerCase();
    });
}

// CodeMismatchException
